// iter12: modal de compartilhar/enviar uma tela (TelaInterna ou Dashboard) por email/telegram.
// Captura PNG via html2canvas e anexa.
// Uso: <CompartilharModal aberto onClose targetRef={ref} titulo="..." textoPadrao="..."/>

import React, { useContext, useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import { GlobalContext } from '../lib/GlobalVar';
import { EnviaNotificacao, ConsultaTelegram } from '../lib/api';
import Swal from 'sweetalert2';
import Icon from './Icon';

const CANAIS = [
    { v: 'email',    l: 'E-mail' },
    { v: 'telegram', l: 'Telegram' },
    { v: 'ambos',    l: 'E-mail + Telegram' },
];

export default function CompartilharModal({ aberto, onClose, targetRef, titulo = '', textoPadrao = '' }) {
    const { user } = useContext(GlobalContext);
    const [canal, setCanal] = useState('email');
    const [destinoEmail, setDestinoEmail] = useState(user?.email || '');
    const [setor, setSetor] = useState('');
    const [assunto, setAssunto] = useState('');
    const [mensagem, setMensagem] = useState('');
    const [incluirPrint, setIncluirPrint] = useState(true);
    const [setoresDisp, setSetoresDisp] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [preview, setPreview] = useState('');

    // reseta os campos toda vez que abre
    useEffect(() => {
        if (!aberto) return;
        setAssunto(`Babita · ${titulo || 'compartilhamento'}`);
        setMensagem(textoPadrao || `Confira a tela "${titulo}".`);
        setDestinoEmail(user?.email || '');
        setCanal('email');
        setSetor('');
        setIncluirPrint(true);
        setPreview('');
        // carrega setores disponiveis (canais Telegram cadastrados)
        ConsultaTelegram({}).then(r => {
            const canais = r?.canais || (Array.isArray(r) ? r : []);
            const setores = [...new Set(canais.map(c => c.setor).filter(Boolean))];
            setSetoresDisp(setores);
            if (setores.length && !setor) setSetor(setores[0]);
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto, titulo, textoPadrao]);

    async function capturaPng() {
        if (!targetRef?.current) return null;
        // ignora elementos fora do alvo (ex.: o proprio modal/FAB) — html2canvas usa o root passado
        const canvas = await html2canvas(targetRef.current, {
            backgroundColor: '#ffffff',
            scale: 1.2,
            logging: false,
            useCORS: true,
            // pula nodes com data-no-capture
            ignoreElements: el => el.hasAttribute && el.hasAttribute('data-no-capture'),
        });
        return canvas.toDataURL('image/png');
    }

    async function gerarPreview() {
        try {
            const dataUrl = await capturaPng();
            setPreview(dataUrl || '');
        } catch (e) {
            Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Falha ao gerar preview: ' + (e.message || e) });
        }
    }

    async function enviar() {
        if (canal === 'email' || canal === 'ambos') {
            if (!destinoEmail || !/^\S+@\S+\.\S+$/.test(destinoEmail)) {
                return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'E-mail de destino inválido.' });
            }
        }
        if (canal === 'telegram' || canal === 'ambos') {
            if (!setor) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Escolha o setor (Telegram).' });
        }
        if (!mensagem.trim() && !incluirPrint) {
            return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Inclua mensagem ou marque "anexar print".' });
        }

        setEnviando(true);
        try {
            let anexo_png_b64 = null;
            if (incluirPrint) {
                const dataUrl = await capturaPng();
                if (dataUrl) anexo_png_b64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
            }
            const resp = await EnviaNotificacao({
                solicitante: user?.email,
                canal,
                destino: destinoEmail,
                setor,
                assunto,
                mensagem,
                anexo_png_b64,
                anexo_nome: `babita-${(titulo || 'tela').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`,
            });
            if (resp.Error) {
                Swal.fire({ icon: 'error', title: 'Falha ao enviar', text: resp.Error });
                return;
            }
            // monta resumo amigavel
            let msgOk = '';
            if (resp.email) msgOk += `E-mail: ${resp.email.accepted?.length || 0} aceito(s). `;
            if (Array.isArray(resp.telegram)) {
                const ok = resp.telegram.filter(x => x.ok).length;
                const fail = resp.telegram.length - ok;
                msgOk += `Telegram: ${ok} ok${fail ? `, ${fail} falha(s)` : ''}.`;
            }
            Swal.fire({ icon: 'success', title: 'Enviado', text: msgOk || 'Notificação enviada.', timer: 2400, showConfirmButton: false });
            onClose();
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Falha', text: e.message || String(e) });
        } finally {
            setEnviando(false);
        }
    }

    if (!aberto) return null;

    const podeEmail = canal === 'email' || canal === 'ambos';
    const podeTelegram = canal === 'telegram' || canal === 'ambos';

    return (
        <div className="modal-mask" data-no-capture onClick={onClose}>
            <div className="modal" style={{ maxWidth: 640 }} data-no-capture onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Compartilhar · {titulo || 'tela'}</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <div className="field">
                        <label>Canal</label>
                        <select className="select" value={canal} onChange={e => setCanal(e.target.value)}>
                            {CANAIS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                        </select>
                    </div>

                    {podeEmail && (
                        <div className="field">
                            <label>E-mail de destino</label>
                            <input className="input mono" value={destinoEmail} onChange={e => setDestinoEmail(e.target.value)} placeholder="exemplo@babita.com.br"/>
                            <span className="muted" style={{ fontSize: 11 }}>SMTP precisa estar configurado em TI · Telegram &gt; SMTP.</span>
                        </div>
                    )}

                    {podeTelegram && (
                        <div className="field">
                            <label>Setor (Telegram)</label>
                            <select className="select" value={setor} onChange={e => setSetor(e.target.value)}>
                                <option value="">— escolha —</option>
                                {setoresDisp.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <span className="muted" style={{ fontSize: 11 }}>
                                {setoresDisp.length === 0
                                    ? 'Nenhum canal Telegram cadastrado. Vá em TI · Telegram > Canais.'
                                    : `Será enviado a todos os canais do setor "${setor || '?'}".`}
                            </span>
                        </div>
                    )}

                    <div className="field">
                        <label>Assunto</label>
                        <input className="input" value={assunto} onChange={e => setAssunto(e.target.value)}/>
                    </div>

                    <div className="field">
                        <label>Mensagem</label>
                        <textarea className="textarea" rows={4} value={mensagem} onChange={e => setMensagem(e.target.value)} placeholder="Texto opcional. Será o caption no Telegram e o corpo do e-mail."/>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6 }}>
                        <input type="checkbox" checked={incluirPrint} onChange={e => setIncluirPrint(e.target.checked)}/>
                        <span><b>Anexar print da tela</b> &mdash; gerado no navegador (html2canvas).</span>
                    </label>

                    {incluirPrint && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                <button className="btn ghost sm" onClick={gerarPreview} disabled={enviando}>
                                    <Icon name="eye" size={11}/> Gerar preview
                                </button>
                                {preview && <span className="muted" style={{ fontSize: 11 }}>{Math.round(preview.length * 3 / 4 / 1024)} KB</span>}
                            </div>
                            {preview && (
                                <div style={{ border: '1px solid var(--line)', padding: 6, background: 'var(--bg-2)', maxHeight: 220, overflow: 'auto' }}>
                                    <img src={preview} alt="preview" style={{ maxWidth: '100%', display: 'block' }}/>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose} disabled={enviando}>Cancelar</button>
                    <button className="btn sm" onClick={enviar} disabled={enviando}>
                        {enviando ? 'Enviando…' : <><Icon name="send" size={11}/> Enviar</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
